import React from 'react';
import PropTypes from 'prop-types';
import { get } from 'lodash';
import { SingleDatePicker, SingleDatePickerWithKey, DayPickerSingleDateControllerWithKey } from 'sarsaparilla';
import moment from 'moment';
import * as types from '../../constants';
import { isDayBeingBlockedByLottery, isDayManagedByLottery, isDayBlocked, isDayWalkupOnly, AVAILABILITY_PROPTYPES } from '../../utils/availability-utils';

// Note: this file is the one used by EXTERNAL permits
export default class SingleDatePickerCalendar extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      focused: false
    };
    this.handleFocusChange = this.handleFocusChange.bind(this);
  }

  getDayType = (day) => {
    if (isDayWalkupOnly(day, this.props)) {
      return 'walkup'
    }
    else if (isDayManagedByLottery(day, this.props)) {
      return 'lottery'
    }
    else if (isDayBlocked(day, this.props)) {
      return 'blocked';
    }
    return 'available';
  }

  getTooltipContent = (day) => {
    let tooltip = 'Available';
    if (isDayWalkupOnly(day, this.props)) {
      return ["This day is walkup only."];
    }
    else if (isDayManagedByLottery(day, this.props)) {
      if (isDayBlocked(day, this.props)) {
        tooltip = ["This lottery day is unavailable"];
      }
      if (isDayBeingBlockedByLottery(day, this.props)) {
        tooltip = ["This day is being managed by the lottery. Please refer to important dates for application process."];
      }
      return tooltip;
    }
    else if (isDayBlocked(day, this.props)) {
      return "Unavailable";
    }

    const tooltips = ['Available'];
    if (!this.props.availabilityMap) {
      return tooltips;
    }

    const date = moment(day).utc().format();

    for (const quota in this.props.quotas) {
      if (Object.prototype.hasOwnProperty.call(this.props.quotas, quota)) {
        let { remaining = 0, total = 0 } = get(this.props, `quotas[${quota}][${date}]`, {})
        if (remaining > 99) {
          remaining = '99+';
        }
        else if (remaining < 0) {
          remaining = 0;
        }
        if (total > 99) {
          total = '99+';
        }
        else if (total < 0) {
          total = 0;
        }

        const remainingTotalString = `${remaining} out of ${total}`;
        if (quota === types.GROUP_AVAILABILITY_KEY_NAME || quota === types.GROUP_AVAILABILITY_DAILY_KEY_NAME) {
          tooltips.push(`\u000AGroups: ${remainingTotalString}`);
        }
        if (quota === types.MEMBER_AVAILABILITY_KEY_NAME) {
          tooltips.push(`\u000APeople: ${remainingTotalString}`);
        }
        if (quota === types.WATERCRAFT_AVAILABILITY_KEY_NAME) {
          tooltips.push(`\u000AWatercraft: ${remainingTotalString}`);
        }
        if (quota === types.MAX_STOCK_KEY_NAME) {
          tooltips.push(`\u000AStock: ${remainingTotalString}`);
        }
      }
    }

    return tooltips.map((t, i) => (
      <div className='tooltip-text' key={t} >
          {t}{i !== tooltips.length - 1 && <span className="rec-sr-only">,</span>}
      </div>
    ));
  }

  isEmptyObject(obj) {
    return (obj === null || typeof obj === "undefined" || (Object.keys(obj).length === 0 && obj.constructor === Object));
  }

  handleFocusChange({ focused }){
    this.setState({ focused });
  }

  render() {
    const props = {
      readOnly: this.props.readOnly,
      disabled: this.state.disabled,
      screenReaderInputMessage: 'Select date',
      daySize: 40,
      initialVisibleMonth: () => {
        return this.props.initialVisibleMonth instanceof moment ?
          this.props.initialVisibleMonth.utc() :
          moment(this.props.initialVisibleMonth).utc();
      },
      isOutsideRange: this.props.isOutsideRange
    };

    if (this.props.ignoreQuotaRestrictions) {
      // Keeping the methods here, since isDayBlocked change doesn't seem to trigger a re-render on DatePicker
      // Was a problem when moving the isDayBlocked method from `DayPickerSingleDateControllerWithKey`
      // Ex. when this.props passed into isDayBlocked changes, there's no update to the calendar
      const isDayBlockedIQR = (day) => !this.props.dateMap[day.utc().format(types.AVAILABILITY_KEY_MAP_FORMAT)];
      const getDayTypeIQR = (day) => (isDayBlockedIQR(day) ? 'blocked' : 'available');
      return (
        <SingleDatePickerWithKey
          {...props}
          date={this.isEmptyObject(this.props.selectedDate) ? null : moment(this.props.selectedDate).utc()}
          onDateChange={(date) => date && this.props.handleDateChange(date, this.props.id)}
          focused={this.state.focused}
          onFocusChange={this.handleFocusChange}
          id={this.props.id ? this.props.id : "selectedDateId"}
          numberOfMonths={1}
          isDayBlocked={isDayBlockedIQR}
          getDayType={getDayTypeIQR} />
      );
    }
    
    if (this.props.calendarOnly) {
      const { readOnly, disabled, screenReaderInputMessage, ...restProps } = props;
      return (
        <DayPickerSingleDateControllerWithKey
          {...restProps}
          date={this.props.selectedDate}
          onDateChange={(selectedDate) => selectedDate && this.props.handleDateChange(selectedDate)}
          onFocusChange={() => {}}
          focused
          isDayBlocked={(day) => isDayBlocked(day, this.props)}
          numberOfMonths={1}
          getDayType={this.getDayType}
          getTooltipContent={this.getTooltipContent} />
      );
    }
    
    return (
      <SingleDatePicker
        {...props}
        date={this.isEmptyObject(this.props.selectedDate) ? null : moment(this.props.selectedDate).utc()}
        onDateChange={(date) => date && this.props.handleDateChange(date, this.props.id)}
        focused={this.state.focused}
        onFocusChange={this.handleFocusChange}
        id={this.props.id ? this.props.id : "selectedDateId"}
        numberOfMonths={1}
        isDayBlocked={(day) => isDayBlocked(day, this.props)}
        getDayType={this.getDayType}
        getTooltipContent={this.getTooltipContent} />
    );
  }
}

SingleDatePickerCalendar.propTypes = {
  focused: PropTypes.bool,
  ...AVAILABILITY_PROPTYPES
};

SingleDatePickerCalendar.defaultProps = {
  ignoreQuotaRestrictions: false,
  calendarOnly: false,
  blockNonLotteryDates: false,
  blockLotteryDates: false,
  currentDate: undefined,
  permit: {},
  dateMap: {},
  availabilityMap: undefined,
  selectedDate: {},
  handleDateChange: () => (true),
  id: undefined,
  readOnly: undefined,
  disabled: undefined,
  renderLabels: true,
  initialVisibleMonth: moment().utc()
};
